import { useState, useEffect } from 'react';
import { api, ClickFunnelsIntegration, CFTeam, CFWorkspace, CFCourse } from '../lib/api';
import { X, ChevronRight, ChevronLeft, Loader2, CheckCircle, AlertCircle, Users, Building2, BookOpen } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

type Step = 'connection' | 'team' | 'workspace' | 'courses' | 'importing' | 'complete';

interface ImportResult {
  courseName: string;
  lessonsImported: number;
  errors: string[];
}

export default function ClickFunnelsCourseImportModal({ isOpen, onClose, onImportComplete }: Props) {
  const [step, setStep] = useState<Step>('connection');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Data states
  const [integrations, setIntegrations] = useState<ClickFunnelsIntegration[]>([]);
  const [selectedIntegration, setSelectedIntegration] = useState<ClickFunnelsIntegration | null>(null);
  const [teams, setTeams] = useState<CFTeam[]>([]);
  const [, setSelectedTeam] = useState<CFTeam | null>(null);
  const [workspaces, setWorkspaces] = useState<CFWorkspace[]>([]);
  const [, setSelectedWorkspace] = useState<CFWorkspace | null>(null);
  const [courses, setCourses] = useState<CFCourse[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<Set<number>>(new Set());
  const [importResults, setImportResults] = useState<ImportResult[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadIntegrations();
    }
  }, [isOpen]);

  async function loadIntegrations() {
    try {
      setLoading(true);
      const data = await api.getClickFunnelsIntegrations();
      setIntegrations(data.integrations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }

  async function loadTeams(integrationId: string) {
    try {
      setLoading(true);
      setError('');
      const data = await api.getClickFunnelsTeams(integrationId);
      setTeams(data.teams);
      if (data.teams.length === 0) {
        setError('No teams found. Verify your API key has the correct permissions.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }

  async function loadWorkspaces(integrationId: string, teamId: number) {
    try {
      setLoading(true);
      setError('');
      const data = await api.getClickFunnelsWorkspaces(integrationId, teamId);
      setWorkspaces(data.workspaces);
      if (data.workspaces.length === 0) {
        setError('No workspaces found in this team.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }

  async function loadCourses(integrationId: string, workspaceId: number) {
    try {
      setLoading(true);
      setError('');
      const data = await api.getClickFunnelsCourses(integrationId, workspaceId);
      setCourses(data.courses);
      if (data.courses.length === 0) {
        setError('No courses found in this workspace.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    if (!selectedIntegration || selectedCourses.size === 0) return;

    setStep('importing');
    const results: ImportResult[] = [];

    for (const courseId of selectedCourses) {
      const cfCourse = courses.find(c => c.id === courseId);
      if (!cfCourse) continue;

      try {
        // Create the course in our system, linked to the CF integration
        const course = await api.createCourse(
          cfCourse.title,
          cfCourse.description || undefined,
          selectedIntegration.id
        );

        // Fetch all sections and lessons (backend auto-paginates to get ALL)
        const lessonsToImport: Array<{ title: string; description?: string; url: string }> = [];

        const sectionsData = await api.getClickFunnelsSections(selectedIntegration.id, courseId);

        for (const section of sectionsData.sections) {
          // Add a small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 200));

          const lessonsData = await api.getClickFunnelsLessons(selectedIntegration.id, section.id);

          for (const lesson of lessonsData.lessons) {
            // Build the lesson URL using current_path from each level
            let lessonUrl = '';
            if (selectedIntegration.customer_center_url) {
              // Use customer center URL with path components
              let coursePath = cfCourse.current_path;
              if (cfCourse.show_in_community) {
                if (coursePath.startsWith('/courses/')) {
                  coursePath = coursePath.replace('/courses/', '/community/c/');
                } else if (!coursePath.includes('/community/')) {
                  coursePath = `/community/c${coursePath}`;
                }
              }
              lessonUrl = `${selectedIntegration.customer_center_url}${coursePath}${section.current_path}${lesson.current_path}`;
            } else {
              lessonUrl = `https://${selectedIntegration.workspace}.myclickfunnels.com${cfCourse.current_path}${section.current_path}${lesson.current_path}`;
            }

            lessonsToImport.push({
              title: lesson.title,
              description: lesson.description || section.title,
              url: lessonUrl,
            });
          }
        }

        // Import all lessons
        if (lessonsToImport.length > 0) {
          const importResult = await api.importLessonsFromClickFunnels(course.id, lessonsToImport);
          results.push({
            courseName: cfCourse.title,
            lessonsImported: importResult.successful,
            errors: importResult.errors,
          });
        } else {
          results.push({
            courseName: cfCourse.title,
            lessonsImported: 0,
            errors: ['No lessons found in course'],
          });
        }
      } catch (err) {
        results.push({
          courseName: cfCourse.title,
          lessonsImported: 0,
          errors: [err instanceof Error ? err.message : 'Import failed'],
        });
      }
    }

    setImportResults(results);
    setStep('complete');
  }

  function handleSelectIntegration(integration: ClickFunnelsIntegration) {
    setSelectedIntegration(integration);
    setStep('team');
    loadTeams(integration.id);
  }

  function handleSelectTeam(team: CFTeam) {
    setSelectedTeam(team);
    setStep('workspace');
    if (selectedIntegration) {
      loadWorkspaces(selectedIntegration.id, team.id);
    }
  }

  function handleSelectWorkspace(workspace: CFWorkspace) {
    setSelectedWorkspace(workspace);
    setStep('courses');
    if (selectedIntegration) {
      loadCourses(selectedIntegration.id, workspace.id);
    }
  }

  function toggleCourse(courseId: number) {
    const newSelected = new Set(selectedCourses);
    if (newSelected.has(courseId)) {
      newSelected.delete(courseId);
    } else {
      newSelected.add(courseId);
    }
    setSelectedCourses(newSelected);
  }

  function selectAllCourses() {
    setSelectedCourses(new Set(courses.map(c => c.id)));
  }

  function deselectAllCourses() {
    setSelectedCourses(new Set());
  }

  function handleClose() {
    // Reset state
    setStep('connection');
    setSelectedIntegration(null);
    setSelectedTeam(null);
    setSelectedWorkspace(null);
    setSelectedCourses(new Set());
    setImportResults([]);
    setError('');
    onClose();
  }

  function handleComplete() {
    onImportComplete();
    handleClose();
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Import Courses from ClickFunnels</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b">
          {(['connection', 'team', 'workspace', 'courses'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-blue-600 text-white'
                    : ['team', 'workspace', 'courses', 'importing', 'complete'].indexOf(step) > i
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {i + 1}
              </div>
              {i < 3 && <ChevronRight className="w-4 h-4 text-gray-400 mx-1" />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          )}

          {/* Step: Select Connection */}
          {step === 'connection' && !loading && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">Select a ClickFunnels connection to import from:</p>
              {integrations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No connected ClickFunnels accounts found.</p>
                  <p className="text-sm mt-1">Please add and test a connection first.</p>
                </div>
              ) : (
                integrations.map((integration) => (
                  <button
                    key={integration.id}
                    onClick={() => handleSelectIntegration(integration)}
                    className="w-full p-4 text-left border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{integration.name}</div>
                    <div className="text-sm text-gray-500">{integration.workspace}.myclickfunnels.com</div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Step: Select Team */}
          {step === 'team' && !loading && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">Select a team:</p>
              {teams.map((team) => (
                <button
                  key={team.id}
                  onClick={() => handleSelectTeam(team)}
                  className="w-full p-4 text-left border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center gap-3"
                >
                  <Users className="w-5 h-5 text-gray-400" />
                  <span className="font-medium text-gray-900">{team.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Step: Select Workspace */}
          {step === 'workspace' && !loading && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">Select a workspace:</p>
              {workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => handleSelectWorkspace(workspace)}
                  className="w-full p-4 text-left border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center gap-3"
                >
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900">{workspace.name}</div>
                    <div className="text-sm text-gray-500">{workspace.subdomain}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step: Select Courses */}
          {step === 'courses' && !loading && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Select courses to import:</p>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllCourses}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={deselectAllCourses}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {courses.map((course) => (
                  <label
                    key={course.id}
                    className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.has(course.id)}
                      onChange={() => toggleCourse(course.id)}
                      className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{course.title}</span>
                        {course.show_in_community && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                            Community
                          </span>
                        )}
                      </div>
                      {course.description && (
                        <p className="text-sm text-gray-500 mt-1">{course.description}</p>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        Path: {course.current_path}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium text-gray-900">Importing courses...</p>
              <p className="text-sm text-gray-500 mt-1">This may take a few moments</p>
            </div>
          )}

          {/* Step: Complete */}
          {step === 'complete' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Import Complete</h3>
                  <p className="text-sm text-gray-500">
                    {importResults.reduce((sum, r) => sum + r.lessonsImported, 0)} lessons imported across {importResults.length} courses
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {importResults.map((result, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg border ${
                      result.errors.length > 0
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{result.courseName}</span>
                      <span className="text-sm text-gray-600">
                        {result.lessonsImported} lessons imported
                      </span>
                    </div>
                    {result.errors.length > 0 && (
                      <div className="mt-2 text-sm text-yellow-700">
                        {result.errors.map((err, j) => (
                          <p key={j}>{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={() => {
              if (step === 'team') {
                setStep('connection');
                setSelectedIntegration(null);
              } else if (step === 'workspace') {
                setStep('team');
                setSelectedWorkspace(null);
              } else if (step === 'courses') {
                setStep('workspace');
                setSelectedCourses(new Set());
              }
            }}
            className={`flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 ${
              step === 'connection' || step === 'importing' || step === 'complete'
                ? 'invisible'
                : ''
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {step === 'courses' && (
            <button
              onClick={handleImport}
              disabled={selectedCourses.size === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import {selectedCourses.size} Course{selectedCourses.size !== 1 ? 's' : ''}
            </button>
          )}

          {step === 'complete' && (
            <button
              onClick={handleComplete}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
